import { useMemo, useState } from 'react';
import { ChevronRight, Folder, Heart, Plus, Search, Store as StoreIcon } from 'lucide-react';
import type { ImapFolderNode } from '../../lib/api';

export interface FolderTreeClickTarget {
  configId: string;
  folder: ImapFolderNode;
}

interface FolderTreePanelProps {
  /** Drzewo folderów IMAP grupowane per config. */
  configs: Array<{ id: string; name: string; folders: ImapFolderNode[] }>;
  /** Callback: kliknięto folder w drzewie (otwarcie widoku). */
  onFolderClick: (target: FolderTreeClickTarget) => void;
  /** Callback: prawy klik na folderze (otwarcie context menu). */
  onContextMenu: (target: FolderTreeClickTarget, anchor: { x: number; y: number }) => void;
  /** Callback: utwórz folder w koncie (folder === null) lub pod folderem (folder !== null). */
  onCreateFolder?: (configId: string, parentFolder: ImapFolderNode | null) => void;
  /** Callback: toggle ulubionego (serduszko przy folderze). */
  onFavoriteToggle?: (target: FolderTreeClickTarget) => void;
  /** ID aktywnego foldera (podświetlenie). */
  activeFolderId?: string | null;
  /** Klasa dla kontenera (np. max-h-[400px]). */
  className?: string;
  /** Czy pokazywać plakietki z liczbą nieprzeczytanych. Default: true. */
  showBadges?: boolean;
}

/**
 * Panel drzewa folderów IMAP z wyszukiwarką, collapse/expand i right-click.
 * Wszystkie foldery jednego konta są pokazywane w jednej hierarchii per parentPath.
 * Klik na chevron rozwija/zwija. Klik na nazwę otwiera folder. Prawy klik → context menu.
 */
export function FolderTreePanel({
  configs,
  onFolderClick,
  onContextMenu,
  onCreateFolder,
  onFavoriteToggle,
  activeFolderId = null,
  className = '',
  showBadges = true,
}: FolderTreePanelProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  // Filtracja drzewa po query — zwracamy node jeśli on lub którekolwiek dziecko matchuje.
  // Matchy rekursywnie rozszerzają `expanded` żeby drogę do matcha rozwinąć.
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return configs;

    const forceExpanded = new Set<string>();

    const filterNode = (node: ImapFolderNode): ImapFolderNode | null => {
      const children = node.children.map(filterNode).filter((c): c is ImapFolderNode => c !== null);
      const selfMatches = node.name.toLowerCase().includes(q) || node.path.toLowerCase().includes(q);
      if (selfMatches || children.length > 0) {
        if (children.length > 0) forceExpanded.add(node.id);
        return { ...node, children };
      }
      return null;
    };

    const result = configs
      .map(cfg => ({
        ...cfg,
        folders: cfg.folders.map(filterNode).filter((f): f is ImapFolderNode => f !== null),
      }))
      .filter(cfg => cfg.folders.length > 0);

    // Mergujemy force-expanded z istniejącymi — robimy to przez setState w onChange.
    // Tutaj tylko kalkulacja dla renderu, ale rozszerzenie wykonujemy na bieżąco:
    // przy filtrze domyślnie renderujemy ZAWSZE rozwinięte ścieżki do matcha.
    forceExpanded.forEach(id => expanded.add(id));

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configs, searchQuery]);

  const toggleExpand = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const renderNode = (configId: string, node: ImapFolderNode, depth: number): React.ReactElement => {
    const hasChildren = node.children.length > 0;
    const isExpanded = expanded.has(node.id) || searchQuery.trim().length > 0;
    const isActive = activeFolderId === node.id;
    const sortedChildren = [...node.children].sort((a, b) => a.name.localeCompare(b.name, 'pl'));

    return (
      <div key={node.id}>
        <div
          className={`group flex items-center gap-1 rounded-md text-xs transition-colors cursor-pointer ${
            isActive
              ? 'bg-accent-primary/15 text-accent-primary'
              : 'text-text-secondary hover:bg-surface-hover/50 hover:text-text-primary'
          }`}
          style={{ paddingLeft: `${depth * 14 + 4}px` }}
          onClick={() => onFolderClick({ configId, folder: node })}
          onContextMenu={(e) => {
            e.preventDefault();
            // Foldery systemowe (Inbox/Sent/Drafts/Trash/Archive/Junk) nie mają menu —
            // są już w głównym menu i nie można ich customizować ani usuwać.
            if (node.specialUse) return;
            onContextMenu({ configId, folder: node }, { x: e.clientX, y: e.clientY });
          }}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (hasChildren) toggleExpand(node.id);
            }}
            className={`flex items-center justify-center w-4 h-5 flex-shrink-0 ${
              hasChildren ? 'text-text-muted hover:text-text-primary' : 'invisible'
            }`}
            aria-label={isExpanded ? 'Zwiń' : 'Rozwiń'}
          >
            <ChevronRight
              className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            />
          </button>
          <Folder className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="flex-1 truncate py-1.5" title={node.path}>
            {node.name}
          </span>
          {onFavoriteToggle && !node.specialUse && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onFavoriteToggle({ configId, folder: node });
              }}
              className={`p-1 rounded hover:bg-surface-muted transition-all flex-shrink-0 ${
                node.isFavorite
                  ? 'text-warning opacity-100'
                  : 'text-text-muted hover:text-warning opacity-0 group-hover:opacity-100'
              }`}
              title={node.isFavorite ? 'Usuń z ulubionych' : 'Dodaj do ulubionych'}
            >
              <Heart className={`w-3 h-3 ${node.isFavorite ? 'fill-neon-pink' : ''}`} />
            </button>
          )}
          {onCreateFolder && !node.specialUse && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onCreateFolder(configId, node);
              }}
              className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-surface-muted text-text-muted hover:text-accent-primary transition-all flex-shrink-0"
              title={`Utwórz podfolder w "${node.name}"`}
            >
              <Plus className="w-3 h-3" />
            </button>
          )}
          {showBadges && node.unreadCount > 0 && (
            <span
              className="text-[10px] text-warning flex-shrink-0 pr-1.5 tabular-nums"
              title={`${node.unreadCount} nieprzeczytanych`}
            >
              {node.unreadCount}
            </span>
          )}
        </div>
        {hasChildren && isExpanded && (
          <div>{sortedChildren.map(child => renderNode(configId, child, depth + 1))}</div>
        )}
      </div>
    );
  };

  return (
    <div className={`flex flex-col ${className}`}>
      <div className="relative px-2 py-1.5 flex-shrink-0">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-3 h-3 text-text-muted pointer-events-none" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Szukaj folderu…"
          className="w-full bg-surface-raised/50 text-xs text-text-primary border border-border-subtle rounded-md pl-7 pr-2 py-1.5 outline-none focus:border-accent-primary/30 placeholder-text-disabled"
        />
      </div>
      <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
        {filtered.length === 0 ? (
          <div className="px-3 py-4 text-center text-[11px] text-text-disabled">
            {searchQuery.trim() ? 'Brak wyników' : 'Brak folderów'}
          </div>
        ) : (
          filtered.map(cfg => (
            <div key={cfg.id} className="pb-2">
              <div className="group flex items-center gap-2 px-2 pt-2 pb-1">
                <StoreIcon className="w-3 h-3 text-text-secondary flex-shrink-0" />
                <span className="flex-1 text-[11px] font-semibold text-text-secondary truncate" title={cfg.name}>
                  {cfg.name}
                </span>
                {onCreateFolder && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCreateFolder(cfg.id, null);
                    }}
                    className="p-1 rounded hover:bg-surface-muted text-text-muted hover:text-accent-primary transition-all flex-shrink-0"
                    title={`Utwórz folder w koncie ${cfg.name}`}
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                )}
              </div>
              {[...cfg.folders]
                .sort((a, b) => a.name.localeCompare(b.name, 'pl'))
                .map(folder => renderNode(cfg.id, folder, 0))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
